import React from 'react';
import { ListItemButton as MuiListItemButton, ListItemButtonProps, Stack } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import cn from 'classnames';

import { isNumber } from '../../../submodules/web-shared/utils/utils';
import { createSx } from '../../utils';
import EllipsedTypography from '../Tooltip/EllipsedTypography';
import Badge, { BadgeProps } from '../Badge';

const sx = createSx({
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  stack: {
    overflow: 'hidden',
    height: 'inherit',
    '& svg': {
      color: 'neutral.onSurfaceVariant'
    }
  },
  labelContainer: {
    overflow: 'hidden',
    '&.showBadge': {
      pr: 2,
      py: 1.5
    }
  },
  label: {
    overflow: 'hidden',
    color: 'neutral.onSurface',
    mr: 1
  },
});

interface Props extends ListItemButtonProps {
  label: string;
  testID?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  labelBadgeVariant?: BadgeProps['variant'];
  labelBadgeCount?: number;
  children?: React.ReactNode;
}

interface ListItemButtonPropsWithoutLabel extends Omit<Props, 'label' | 'startIcon'> {
  label?: never;
  startIcon: React.ReactNode;
}

const ListItemButton = ({
  label,
  startIcon,
  endIcon,
  labelBadgeVariant,
  labelBadgeCount,
  children,
  testID,
  ...props }: Props | ListItemButtonPropsWithoutLabel) => {

  const showBadge = labelBadgeVariant || isNumber(labelBadgeCount);
  return (
    <MuiListItemButton
      sx={sx.container}
      aria-selected={props.selected || undefined}
      data-test-id={testID}
      {...props}
    >
      <Stack
        sx={sx.stack}
        direction='row'
        alignItems='center'
        spacing={0.5}
      >
        {startIcon}
        <Stack sx={sx.labelContainer} className={cn({ showBadge })}>
          <Badge
            variant={labelBadgeVariant}
            badgeContent={labelBadgeCount}
            showZero
            invisible={!isNumber(labelBadgeCount) && labelBadgeVariant !== 'dot'}
            size='small'
            data-test-id={`${testID}-badge`}
          >
            <EllipsedTypography
              sx={sx.label}
              variant='body2'
              testID='option-item-label'
            >
              {label}
            </EllipsedTypography>
          </Badge>
          {children}
        </Stack>
      </Stack>
      {(props.selected && !endIcon) && <CheckIcon className='selected-option-icon' />}
      {endIcon}
    </MuiListItemButton>
  );
};

export default ListItemButton;